/**
 * Auto-route orchestrator (spec 09): compute the search window, load depth tiles,
 * run the router worker, return editor-ready waypoints. Abortable.
 */
import type { LatLon, Bbox } from '@boatnav/core';
import type { Boat } from '../../lib/db.js';
import { loadDepthWindow } from './depthProvider.js';

const MIN_MARGIN_DEG = 0.045; // ~5 km
const MAX_NODES = 300_000;

export type AutoRouteResult = { waypoints: LatLon[] } | { error: string };

/** bbox(A,B) inflated 50% (min ~5 km margin), spec 09. */
function searchWindow(a: LatLon, b: LatLon): Bbox {
  const west = Math.min(a.lon, b.lon);
  const east = Math.max(a.lon, b.lon);
  const south = Math.min(a.lat, b.lat);
  const north = Math.max(a.lat, b.lat);
  const mx = Math.max((east - west) * 0.5, MIN_MARGIN_DEG);
  const my = Math.max((north - south) * 0.5, MIN_MARGIN_DEG);
  return { west: west - mx, east: east + mx, south: south - my, north: north + my };
}

export async function computeAutoRoute(
  start: LatLon,
  goal: LatLon,
  boat: Boat,
  signal?: AbortSignal,
): Promise<AutoRouteResult> {
  const win = await loadDepthWindow(searchWindow(start, goal));
  if (signal?.aborted) return { error: 'aborted' };

  const worker = new Worker(new URL('./router.worker.ts', import.meta.url), { type: 'module' });
  const requiredDepth = boat.draughtM + (boat.clearanceM ?? 0.5);

  return new Promise<AutoRouteResult>((resolve) => {
    const cleanup = (): void => {
      worker.terminate();
      signal?.removeEventListener('abort', onAbort);
    };
    const onAbort = (): void => {
      cleanup();
      resolve({ error: 'aborted' });
    };
    signal?.addEventListener('abort', onAbort);
    worker.onmessage = (e: MessageEvent<AutoRouteResult>): void => {
      cleanup();
      resolve(e.data);
    };
    worker.onerror = (): void => {
      cleanup();
      resolve({ error: 'worker' });
    };
    worker.postMessage({ ...win, start, goal, requiredDepth, maxNodes: MAX_NODES });
  });
}
